/**
 * Wrap `fetch` so PostgREST 401s caused by mild client/server clock skew
 * ("JWT issued at future" / PGRST303) get one delayed retry.
 *
 * Why this exists: GoTrue stamps JWT `iat` from the device clock (or a
 * clock that is slightly ahead of PostgREST). When the phone is a few
 * seconds fast, the first REST call after sign-in / resume fails with
 * PGRST303 and our call sites `reportError` → Sentry noise (seen on
 * Mobile Safari for `clubDistances.fetch`). Waiting ~2s lets `iat`
 * become "in the past" relative to PostgREST without forcing a
 * refresh that would mint another ahead-of-time token.
 *
 * Large skew (minutes+) still fails after the retry — that is a real
 * device-clock problem the user must fix; we only absorb the transient
 * case.
 */

const DEFAULT_SKEW_RETRY_MS = 2000;

export function isJwtIssuedAtFutureError(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const rec = body as Record<string, unknown>;
  const code = typeof rec.code === 'string' ? rec.code : '';
  const message = typeof rec.message === 'string' ? rec.message : '';
  if (/issued at future/i.test(message)) return true;
  // PostgREST clock-skew code — match even when the message is generic.
  if (code === 'PGRST303') return true;
  return false;
}

async function readErrorBody(res: Response): Promise<unknown> {
  try {
    const text = await res.clone().text();
    if (!text) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return { message: text };
    }
  } catch {
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type ClockSkewFetchOptions = {
  /** Delay before the single retry. Default 2000ms. */
  retryDelayMs?: number;
  /** Underlying fetch (defaults to globalThis.fetch). */
  fetchImpl?: typeof fetch;
};

/**
 * Returns a fetch function suitable for `createClient({ global: { fetch } })`.
 */
export function createClockSkewTolerantFetch(
  options: ClockSkewFetchOptions = {},
): typeof fetch {
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_SKEW_RETRY_MS;
  const baseFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await baseFetch(input, init);
    if (response.status !== 401) return response;

    const body = await readErrorBody(response);
    if (!isJwtIssuedAtFutureError(body)) return response;

    await delay(retryDelayMs);
    return baseFetch(input, init);
  };
}
